import { props } from '@stylexjs/stylex';
import { getIntents } from '../intent';
import { StyleSheets } from '../stylesheets';
import { VIEW_CLASS } from './constants';
import { StyleCache } from './StyleCache';
import { addClassName, addStyle } from './util';
import { getRegisteredCssSerializer } from '../view/css';
import { runtimeStyleIntent, lazyGlobalStyleIntent, lazyStyleIntent } from './intents';
import { lazyGlobalTokenIntent, lazyTokenIntent } from '../token/intents';
import { tokenCss } from '../token/css';
const scheduledIntents = [];
let lastInsertedIndex = 0;
StyleSheets.onFlush(()=>{
    lastInsertedIndex = 0;
});
export function scheduleStyleIntent(intent) {
    const isRuntimeGlobalIntent = runtimeStyleIntent.isIntent(intent) && intent.layer === 'global';
    if (!isRuntimeGlobalIntent && !lazyGlobalStyleIntent.isIntent(intent) && !lazyGlobalTokenIntent.isIntent(intent)) {
        throw new Error('Only styles on the global layer can be scheduled.');
    }
    scheduledIntents.push(intent);
}
export function runLazyStyleIntents(revision, options, cssPolicy) {
    const css = getRegisteredCssSerializer();
    // Directly insert any scheduled global style intents.
    // We do this because these intents can be scheduled at any time, and
    // therefore are not deterministically associated with rendering the current
    // component. This is safe to do because they are fully global and don't
    // produce class names to be rendered on the current component's element.
    for(let i = lastInsertedIndex; i < scheduledIntents.length; i++){
        const maybeIntent = scheduledIntents[i];
        let intent;
        if (css && lazyGlobalStyleIntent.isIntent(maybeIntent)) {
            intent = css.global(maybeIntent);
        } else if (lazyGlobalTokenIntent.isIntent(maybeIntent)) {
            intent = tokenCss.global(maybeIntent);
        } else if (runtimeStyleIntent.isIntent(maybeIntent)) {
            intent = maybeIntent;
        }
        // Directly insert global styles.
        if (intent) {
            StyleCache.insert([
                intent.key
            ]);
        }
    }
    lastInsertedIndex = scheduledIntents.length;
    const styleIntents = getIntents(lazyStyleIntent, revision) || [];
    const globalStyleIntents = getIntents(lazyGlobalStyleIntent, revision) || [];
    const tokenIntents = getIntents(lazyTokenIntent, revision) || [];
    const globalTokenIntents = getIntents(lazyGlobalTokenIntent, revision) || [];
    const hasIntents = styleIntents.length > 0 || globalStyleIntents.length > 0 || tokenIntents.length > 0 || globalTokenIntents.length > 0;
    if (css && hasIntents) {
        const classnames = [];
        const compiledStyles = [];
        for(let i = 0; i < styleIntents.length; i++){
            if (styleIntents[i]) {
                const [raw, computed] = styleIntents[i];
                switch(cssPolicy){
                    // @ts-expect-error - it's expected that we fall through to the next case
                    case 'static-only':
                        {
                            if (process.env.NODE_ENV === 'development') {
                                if (!computed) {
                                    throw new Error(`The '${options.name || revision.Component}' component is being rendered in 'static-only' mode but runtime styles are being provided. Please either update the 'cssPolicy' to 'static-first | runtime-only' or compile runtime styles: ${JSON.stringify(raw, null, 2)}.`);
                                }
                            }
                        }
                    // eslint-disable-next-line no-fallthrough
                    case 'static-first':
                        {
                            if (computed) {
                                compiledStyles.push(computed);
                            } else {
                                classnames.push(css(raw).key);
                            }
                            break;
                        }
                    case 'runtime-only':
                    default:
                        {
                            classnames.push(css(raw).key);
                            break;
                        }
                }
            }
        }
        for(let i = 0; i < globalStyleIntents.length; i++){
            if (globalStyleIntents[i]) {
                classnames.push(css.global(globalStyleIntents[i]).key);
            }
        }
        for(let i = 0; i < tokenIntents.length; i++){
            if (tokenIntents[i]) {
                classnames.push(tokenCss(tokenIntents[i]).key);
            }
        }
        for(let i = 0; i < globalTokenIntents.length; i++){
            if (globalTokenIntents[i]) {
                classnames.push(tokenCss.global(globalTokenIntents[i]).key);
            }
        }
        if (compiledStyles.length > 0) {
            const { className, style } = props(...compiledStyles);
            if (className) {
                addClassName(revision.props, className);
            }
            if (style) {
                addStyle(revision.props, style);
            }
        }
        addClassName(revision.props, StyleCache.insert(classnames));
    } else {
        addClassName(revision.props, VIEW_CLASS);
    }
}

//# sourceMappingURL=StyleIntent.js.map